/**
 * AddToProductContentMode — Container for the "Inhalte" (Content) tab.
 *
 * COPIED from RemakeApplyContentMode and generalized: instead of a
 * Remake jury result, the suggestion source is the AddToProduct payload's
 * `texts`. The payload is mapped to the SAME data structure the
 * channel-editor stack consumes via `buildSuggestionFieldsFromTexts`
 * (which reuses the existing `remakeSuggestion` helpers — no duplication).
 *
 * Uses the same channel editor stack as the Products-Content-Channels-Tab:
 *   useChannelDescriptions + useChannelEditor + ChannelEditorPanel + DirtyBar
 * "Einsetzen" buttons write into local editFields only; the DirtyBar Save
 * button triggers the actual JTL push (single hexagonal port).
 */

import { useEffect, useState } from 'react'
import { useChannelDescriptions } from '@/hooks/useChannelDescriptions'
import { useChannelEditor } from '@/hooks/useChannelEditor'
import { ChannelPillsSelector } from '@/components/products/v2/shared/ChannelPillsSelector'
import { ChannelEditorPanel } from '@/components/products/v2/tabs/content/channels/ChannelEditorPanel'
import { ChannelEditorDirtyBar } from '@/components/products/v2/tabs/content/channels/ChannelEditorDirtyBar'
import { BulletsEditorSection } from '@/components/products/v2/tabs/content/channels/BulletsEditorSection'
import {
  MASTER_CHANNEL_ID,
  getChannelName,
  type ContentFieldKey,
} from '@/components/products/v2/tabs/content/channels/channel-constants'
import {
  buildRemakeChannelPills,
  resolveChannelName,
} from '@/components/v2/tools/amazon-listing/RemakeApply/remakeChannelPills'
import {
  buildBulletSuggestions,
  buildSuggestionFields,
} from '@/components/v2/tools/amazon-listing/RemakeApply/remakeSuggestion'
import { buildSuggestionFieldsFromTexts } from './contentSuggestions'
import type { AddToProductTextField } from './types'

export interface AddToProductContentModeProps {
  productId: string
  workspaceIntegrationId: string | null
  texts: AddToProductTextField[]
  language?: string
  /**
   * Called whenever the editor's dirty state changes. The reference MUST be
   * stable across renders (a useState setter or useCallback) — an inline
   * arrow that triggers parent state updates can cause a re-render loop.
   */
  onDirtyChange?: (dirty: boolean) => void
}

export function AddToProductContentMode({
  productId,
  texts,
  language = 'de',
  onDirtyChange,
}: AddToProductContentModeProps) {
  const { languages, channelIds, channelNames, pushMutation, getMaster, getChannel } =
    useChannelDescriptions(productId)

  const [selectedChannelId, setSelectedChannelId] = useState<string>(MASTER_CHANNEL_ID)
  const [selectedLang, setSelectedLang] = useState<string>(language)

  useEffect(() => {
    if (languages.length > 0 && !languages.includes(selectedLang)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- language fallback sync (same pattern as ProductContentChannelsTab)
      setSelectedLang(languages[0])
    }
  }, [languages, selectedLang])

  const masterDesc = getMaster(selectedLang)
  const channelDesc = getChannel(selectedChannelId, selectedLang)
  const isMaster = selectedChannelId === MASTER_CHANNEL_ID

  const editor = useChannelEditor({
    channelDescription: isMaster ? masterDesc : channelDesc,
    masterDescription: masterDesc,
    channelId: selectedChannelId,
    selectedLang,
    pushMutation,
    productId,
    includeBullets: true,
  })

  useEffect(() => {
    onDirtyChange?.(editor.isDirty)
  }, [editor.isDirty, onDirtyChange])

  const jury = buildSuggestionFieldsFromTexts(texts)
  const suggestionFields = buildSuggestionFields(jury)
  const bulletSuggestions = buildBulletSuggestions(jury)

  const onApplyField = (key: ContentFieldKey) => {
    const value = suggestionFields[key]
    if (value != null) editor.updateField(key, value)
  }

  const pills = buildRemakeChannelPills({
    channelIds,
    channelNames,
    selectedLang,
    masterDesc,
    getChannel,
  })

  const editorChannelName = isMaster
    ? (channelNames[MASTER_CHANNEL_ID] ?? getChannelName(MASTER_CHANNEL_ID))
    : resolveChannelName(selectedChannelId, channelNames)

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-neutral-50 dark:bg-neutral-950">
      <div className="px-3 pt-3 pb-2 bg-neutral-50 dark:bg-neutral-900/70">
        <ChannelPillsSelector
          channels={pills}
          activeChannelId={selectedChannelId}
          onChange={setSelectedChannelId}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        <ChannelEditorPanel
          productId={productId}
          channelId={selectedChannelId}
          channelName={editorChannelName}
          description={isMaster ? masterDesc : channelDesc}
          masterDescription={masterDesc}
          isMaster={isMaster}
          translateMode={false}
          translateSourceDesc={undefined}
          translateSourceLang={selectedLang}
          targetLang={selectedLang}
          availableLanguages={languages}
          onTargetLangChange={setSelectedLang}
          editFields={editor.editFields}
          inheritanceMap={editor.inheritanceMap}
          overrideCount={editor.overrideCount}
          onFieldChange={editor.updateField}
          onToggleInherit={editor.toggleOverride}
          suggestion={jury ? { fields: suggestionFields, onApplyField } : undefined}
          bulletsSlot={
            <BulletsEditorSection
              available={editor.bulletsAvailable}
              bulletFields={editor.bulletFields}
              bulletSuggestions={jury ? bulletSuggestions : undefined}
              onUpdate={editor.updateBullet}
              isMasterChannel={isMaster}
            />
          }
        />
      </div>

      <ChannelEditorDirtyBar
        isDirty={editor.isDirty}
        isSaving={editor.isSaving}
        changeCount={editor.overrideCount}
        onSave={editor.save}
        onDiscard={editor.discard}
      />
    </main>
  )
}
